/**
 * usePillSpriteEffect — регистрирует stretchable rounded-rect спрайт-подложку
 * `field-pill-bg`, на который ссылаются field-label pill-слои в registry.ts
 * (AK-299 / AK-303), и держит `text-field` pill «номер · площадь» в синхроне с
 * тогглами `labels.number` / `labels.area` (AK-297).
 *
 * Аналог `useCropPatternAtlasEffect`: side-effect addImage'а живёт вне registry
 * (registry декларативен, §6.4). Спрайт ставится один раз после `load` и
 * переустанавливается при смене темы (см. `installPillSprite` — MutationObserver
 * на <html> class/data-theme, т.к. MapLibre не резолвит `hsl(var(--token))`).
 *
 * AK-297 (founder UAT 2026-06-28): чекбокс «Площадь» был де-факто мёртв —
 * text-field всегда конкатенировал номер И площадь, а visibility-предикат слоя
 * (`number || area`) держал pill видимым. Теперь:
 *   1. `installPillSprite` получает live-геттер тогглов → theme-sync собирает
 *      text-field по актуальному toggle-состоянию (не затирает полным concat).
 *   2. Второй useEffect подписан на `labels.number` / `labels.area` и зовёт
 *      `syncNumberAreaTextField` при их изменении (app-state, не MVT-данные —
 *      поэтому imperative setLayoutProperty, как layerSyncEffect для фильтров).
 *
 * Идемпотентно по StrictMode double-mount: `installPillSprite` снимает старый
 * спрайт перед addImage, cleanup отписывает observer + load-listener.
 */
import { useEffect } from 'react';
import type maplibregl from 'maplibre-gl';
import { installPillSprite, syncNumberAreaTextField } from '../pillSprite';
import { useMapStore } from '../useMapStoreV2';

export function usePillSpriteEffect(map: maplibregl.Map | null): void {
  // Sprite registration + theme-sync. Передаём live-геттер тогглов, чтобы
  // theme-re-register собирал text-field по текущему toggle-состоянию.
  useEffect(() => {
    if (!map) return;
    const dispose = installPillSprite(map, () => {
      const { number, area, crop, icon } = useMapStore.getState().labels;
      return { number, area, crop, icon };
    });
    return dispose;
  }, [map]);

  // AK-297: пересобрать text-field pill при смене тогглов «Номер поля» /
  // «Площадь». Per-field selectors — эффект фаерит только когда меняется
  // конкретный слайс (не на каждый render store).
  const showNumber = useMapStore((s) => s.labels.number);
  const showArea = useMapStore((s) => s.labels.area);
  // AK-590: культура и иконка культуры теперь секции ТОЙ ЖЕ пилюли (вторая
  // строка), поэтому их тогглы тоже пересобирают text-field, а не гасят
  // отдельные слои.
  const showCrop = useMapStore((s) => s.labels.crop);
  const showIcon = useMapStore((s) => s.labels.icon);
  useEffect(() => {
    if (!map) return;
    syncNumberAreaTextField(map, {
      number: showNumber,
      area: showArea,
      crop: showCrop,
      icon: showIcon,
    });
  }, [map, showNumber, showArea, showCrop, showIcon]);
}
